import React, { useContext, useEffect, useMemo } from "react";
import { Form, Field, useForm } from "react-final-form";
import { FORM_ERROR } from "final-form";
import { toast } from "sonner";
import { IntlContext } from "context";
import { Button } from "components/ui/button";
import { Dialog, DialogDescription, DialogTitle } from "components/ui/dialog";
import { DialogCard } from "components/ui/dialog-card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import { useCreateReportAd, reportAdKeys } from "hooks/reportAd";
import { useQueryClient } from "@tanstack/react-query";
import { getServerErrorKey, getServerMessage } from "lib/serverError";
import type { ReportAdReasonCode } from "types/reportAd";

type ReportAdDialogProps = {
    adId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

type FormValues = {
    reporting_code: ReportAdReasonCode | "";
    comment: string;
};

const reasonCodes: ReportAdReasonCode[] = [
    "inappropriate_or_offensive",
    "suspected_scam_or_fraud",
    "spam_or_advertising",
    "abnormal_price",
    "other",
];

const FormResetter: React.FC<{ open: boolean }> = ({ open }) => {
    const form = useForm<FormValues>();
    useEffect(() => {
        if (!open) form.reset();
    }, [open, form]);
    return null;
};

const ReportAdDialog: React.FC<ReportAdDialogProps> = ({ adId, open, onOpenChange }) => {
    const intl = useContext(IntlContext);
    const queryClient = useQueryClient();
    const createReportAdMutation = useCreateReportAd();

    const reasons = useMemo(
        () =>
            reasonCodes.map((code) => ({
                value: code,
                label: intl.formatMessage(`ad.view.report_dialog.reason.${code}`),
            })),
        [intl],
    );

    const validate = (values: FormValues) => {
        const errors: Partial<Record<keyof FormValues, string>> = {};

        if (!values.reporting_code) {
            errors.reporting_code = intl.formatMessage("ad.view.report_dialog.error.reason_required");
        }

        if (values.reporting_code === "other" && !values.comment.trim()) {
            errors.comment = intl.formatMessage("ad.view.report_dialog.error.comment_required");
        }

        return errors;
    };

    const onSubmit = async (values: FormValues) => {
        if (!values.reporting_code) return;

        try {
            await createReportAdMutation.mutateAsync({
                adId,
                data: {
                    reporting_code: values.reporting_code,
                    comment: values.comment.trim() || undefined,
                },
            });
            toast.success(intl.formatMessage("ad.view.report_success"));
            onOpenChange(false);
        } catch (err: unknown) {
            const serverErrorKey = getServerErrorKey(err);
            const serverMessage = getServerMessage(err);
            const alreadyReportedMessage = intl.formatMessage("ad.view.report_dialog.error.already_reported");
            const adUnavailableMessage = intl.formatMessage("ad.view.report_dialog.error.ad_unavailable");

            if (serverErrorKey === "already_reported" || serverMessage === alreadyReportedMessage) {
                void queryClient.invalidateQueries({ queryKey: reportAdKeys.status(adId) });
                return { [FORM_ERROR]: alreadyReportedMessage };
            }

            if (serverErrorKey === "ad_unavailable" || serverMessage === adUnavailableMessage) {
                return { [FORM_ERROR]: adUnavailableMessage };
            }

            return { [FORM_ERROR]: serverMessage ?? intl.formatMessage("ad.view.report_dialog.error.generic") };
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogCard closeLabel={intl.formatMessage("ad.view.report_dialog.close")}>
                <Form<FormValues>
                    initialValues={{ reporting_code: "", comment: "" }}
                    validate={validate}
                    onSubmit={onSubmit}
                    render={({ handleSubmit, submitting, submitError, values }) => (
                        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
                            <FormResetter open={open} />

                            <DialogTitle className="pr-12 text-2xl font-black leading-8 text-gray-900">
                                {intl.formatMessage("ad.view.report_dialog.title")}
                            </DialogTitle>
                            <DialogDescription className="sr-only">
                                {intl.formatMessage("ad.view.report_dialog.description")}
                            </DialogDescription>

                            <Field<ReportAdReasonCode | ""> name="reporting_code">
                                {({ input, meta }) => (
                                    <div className="flex flex-col gap-2">
                                        <label htmlFor="report-ad-reason" className="text-sm font-bold text-gray-800">
                                            {intl.formatMessage("ad.view.report_dialog.reason_label")}
                                        </label>
                                        <Select
                                            value={input.value}
                                            onValueChange={(value) => input.onChange(value)}
                                        >
                                            <SelectTrigger
                                                id="report-ad-reason"
                                                aria-invalid={meta.touched && Boolean(meta.error)}
                                                aria-describedby={meta.touched && meta.error ? "report-ad-reason-error" : undefined}
                                            >
                                                <SelectValue placeholder={intl.formatMessage("ad.view.report_dialog.reason_placeholder")} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {reasons.map((reason) => (
                                                    <SelectItem key={reason.value} value={reason.value}>
                                                        {reason.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {meta.touched && meta.error && (
                                            <p id="report-ad-reason-error" className="text-sm font-semibold text-red-600">
                                                {meta.error}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </Field>

                            <Field<string> name="comment">
                                {({ input, meta }) => (
                                    <div className="flex flex-col gap-2">
                                        <label htmlFor="report-ad-comment" className="text-sm font-bold text-gray-800">
                                            {intl.formatMessage("ad.view.report_dialog.comment_label")}
                                            {values.reporting_code === "other" ? " *" : ""}
                                        </label>
                                        <Textarea
                                            id="report-ad-comment"
                                            value={input.value}
                                            maxLength={1000}
                                            aria-invalid={meta.touched && Boolean(meta.error)}
                                            aria-describedby={meta.touched && meta.error ? "report-ad-comment-error" : undefined}
                                            onChange={input.onChange}
                                            onBlur={input.onBlur}
                                        />
                                        {meta.touched && meta.error && (
                                            <p id="report-ad-comment-error" className="text-sm font-semibold text-red-600">
                                                {meta.error}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </Field>

                            <div aria-live="polite">
                                {submitError && <p className="text-sm font-semibold text-red-600">{submitError}</p>}
                            </div>

                            <Button type="submit" disabled={submitting}>
                                {intl.formatMessage("ad.view.report_dialog.submit")}
                            </Button>
                        </form>
                    )}
                />
            </DialogCard>
        </Dialog>
    );
};

export default ReportAdDialog;
